import { useState } from 'react';

import { useLocation } from 'react-router';

import {
  useCurrentUser,
  useLegalConfig,
  useNeedsConsent,
  useUpdateConsent,
  useUserDocument,
} from '@chartai/api-client';

import { Button } from '@/shared/components/ui/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog';

const EXEMPT_PATH_PREFIXES = [
  '/legal',
  '/release-notes',
  '/how-to-videos',
  '/dashboard/profile',
  '/subscriptions',
];

const DISMISSABLE_PATHS = ['/subscriptions', '/dashboard/profile'];

function isExemptPath(pathname: string): boolean {
  return EXEMPT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function isDismissablePath(pathname: string): boolean {
  return DISMISSABLE_PATHS.some((prefix) => pathname.startsWith(prefix));
}

export function LegalConsentModal() {
  const [accepted, setAccepted] = useState(false);
  const location = useLocation();
  const { user } = useCurrentUser();
  const { data: userDoc } = useUserDocument(user?.uid ?? null);
  const { data: legalConfig } = useLegalConfig();
  const needsConsent = useNeedsConsent(userDoc ?? null, legalConfig ?? null);
  const { mutate: updateConsent, isPending } = useUpdateConsent();

  if (!needsConsent || isExemptPath(location.pathname)) {
    return null;
  }

  if (!user || !legalConfig) {
    return null;
  }

  const dismissable = isDismissablePath(location.pathname);

  const handleAccept = () => {
    if (!legalConfig) return;
    updateConsent({
      uid: user.uid,
      termsAcceptedVersion: String(legalConfig['termsVersion'] ?? ''),
      privacyAcceptedVersion: String(legalConfig['privacyVersion'] ?? ''),
      acceptedPlatform: 'web',
    });
  };

  return (
    <Dialog open onOpenChange={dismissable ? undefined : () => undefined}>
      <DialogContent
        onPointerDownOutside={
          dismissable ? undefined : (e) => e.preventDefault()
        }
        onEscapeKeyDown={dismissable ? undefined : (e) => e.preventDefault()}
        className={dismissable ? '' : '[&>button[aria-label="Close"]]:hidden'}
      >
        <DialogHeader>
          <DialogTitle>Updated Terms</DialogTitle>
          <DialogDescription>
            Our Terms of Service and Privacy Policy have been updated. Please
            review and accept to continue using ChartAI.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 rounded-lg bg-muted px-4 py-3">
          <Checkbox
            id="legal-consent"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <label htmlFor="legal-consent" className="text-sm text-foreground">
            I accept the updated Terms and Privacy Policy
          </label>
        </div>

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!accepted || isPending}
          >
            {isPending ? 'Saving...' : 'Accept & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
